import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authService } from '../services/authService';
import { setUser } from '../store/authSlice';
import { RootState } from '../store/store';

const EmailVerification: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSendVerification = async () => {
    try {
      setLoading(true);
      setMessage('');
      setError('');
      
      const response = await authService.sendVerificationEmail();
      setMessage(response.message);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      setMessage('');
      setError('');
      
      const response = await authService.verifyEmail(user?.email || '', verificationCode);
      
      // Update user in Redux store with verified status
      if (response.user) {
        dispatch(setUser(response.user));
      }
      
      setMessage('Email verified successfully!');
      setVerificationCode('');
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.isEmailVerified) {
    return null; // Don't show if user is not logged in or already verified
  }

  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4">
      <div className="flex items-start">
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">Email Verification Required</h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>Please verify your email address to access all features.</p>
            
            {message && (
              <div className="mt-2 text-sm text-green-600">{message}</div>
            )}
            
            {error && (
              <div className="mt-2 text-sm text-red-600">{error}</div>
            )}
            
            <form onSubmit={handleVerifyEmail} className="mt-3">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="Enter verification code"
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  maxLength={6}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                >
                  Verify
                </button>
              </div>
            </form>
            
            <div className="mt-3">
              <button
                onClick={handleSendVerification}
                disabled={loading}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Resend verification code'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;